import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import T from 'utils/useLocalizer';
import { buttons } from 'utils/resources';
import { ISelectItem, StatusClassItem } from 'models/commonModel';
import ModalWrapper, { ModalFooter } from 'components/ui/modal/ModalWrapper';
import SubmitButton from 'components/form/button/SubmitButton';
import SelectField from 'components/form/input/SelectField';
import { IOrderDetails, IOrderFormData, ORDER_STATUS } from 'models/ordersModel';
import FormLabel from 'components/form/label/FormLabel';
import { useGetOrderById, useGetStatusListByOrderId } from 'services/orders/ordersServices';
import { useLanguage } from 'utils/useLanguage';
import { Calendar, CircleFill, Telephone, BoxSeam } from 'react-bootstrap-icons';
import { getLocalDate, getStatusClass } from 'utils/utils';
import CustomSelectField from 'components/form/input/CustomSelectField';

interface Props {
    item: IOrderDetails | undefined;
    isLoading: boolean;
    isFetchingOrder: boolean;
    id: number;
    systemStatusId: number | undefined
    show: boolean;
    onSubmit: (id: number, data: IOrderFormData, isNotify: boolean, isStatusChanged: boolean, notificationPhoneNumber?: string, phoneNumber?: string, waTemplateId?: number
        , templateName?: string, templateLanguage?: string) => any;
    setOpenUpdateModal: (state: boolean) => any;
}

const schema: yup.ObjectSchema<IOrderFormData> = yup.object({
    statusId: yup.string().required().min(1),
    note: yup.string().max(512)
});

const UpdateOrderModal = ({ item, systemStatusId, id, show, onSubmit, setOpenUpdateModal, isLoading, isFetchingOrder }: Props) => {
    const { language } = useLanguage();
    const { data: statusList, isFetching: isFetchingStatusList } = useGetStatusListByOrderId(language);

    const {
        register,
        handleSubmit,
        watch,
        reset,
        formState: { errors, isValid },
    } = useForm<IOrderFormData>({
        defaultValues: {
            note: item?.note,
            statusId: item?.statusId
        },
        resolver: yupResolver(schema),
        mode: 'onSubmit',
    });

    const orderStatusClasses: Array<StatusClassItem> = [
        {
            status: ORDER_STATUS.Confirmed,
            className: 'badge bg-light-blue',
        },
        {
            status: ORDER_STATUS.Completed,
            className: 'badge bg-light-green',
        },
        {
            status: ORDER_STATUS.Cancelled,
            className: 'badge bg-light-red',
        },
    ];

    useEffect(() => {
        if (!item || !show || !statusList?.length) return;
        reset({
            statusId: item.statusId.toString() ?? '',
            note: item.note ?? ''
        });
    }, [item, show, reset,statusList]);
 
    const selectStatusList: ISelectItem<number>[] = statusList
        ? statusList
            .filter(status =>
                systemStatusId === ORDER_STATUS.Confirmed
                    ? true
                    : status.orderSystemStatusId !== ORDER_STATUS.Confirmed
            )
            .map(status => ({
                value: status.id,
                label: status.name,
                icon: <CircleFill
                    width={8}
                    height={8}
                    className={getStatusClass(status.orderSystemStatusId ?? 0, orderStatusClasses, { defaultClass: 'badge bg-light-blue' })
                        .replace('badge ', '')   // reuse your existing class logic for color
                    }
                />
            }))
        : [];


    const resources = {
        modalTitle: T('order.update.modal.title'),
        orderLabel: T('order.update.modal.label'),
        customerLabel: T('order.customer.update.modal.label'),
        orderInfoLabel: T('order.info.update.modal.label'),
        itemsLabel: T('order.items.update.modal.label'),
        totalLabel: T('order.total.update.modal.label'),
        noteLabel: T('order.form.note.label'),
        notePlaceholder: T('order.form.note.placeholder'),
        statusLabel: T('order.status.form.label'),
        quantityLabel: T('order.quantity.update.modal.label'),
        submitButton: T(buttons.submit),
        cancelButton: T(buttons.cancel),
    };
    const statusWatch = watch('statusId');
    const noteWatch = watch('note');

    const onSubmitHandler = async (data: IOrderFormData) => {
        setOpenUpdateModal(false)
        reset()
        const selectedStatus = statusList?.find(
            x => x.id === Number(data.statusId)
        );
        const isNotify = selectedStatus?.isNotify ?? false;
        const waTemplateId = selectedStatus?.waTemplateId;
        const templateName = selectedStatus?.templateName;
        const templateLanguage = selectedStatus?.templateLanguage;
        const isStatusChanged = item?.statusId != data.statusId
        const result = await onSubmit(id, data, isNotify ?? false, isStatusChanged, item?.merchantInfo?.notificationPhoneNumber,
            item?.phoneNumber, waTemplateId, templateName, templateLanguage);

        // if (result) {
        // //     reset({
        // //     statusId: data.statusId.toString() ?? '',
        // //     note: data.note ?? ''
        // // });
        // refetch()
        // }
    }

    const onCancelHandler = () => {
        setOpenUpdateModal(false)
        reset()
    }

    return (
        <ModalWrapper
            show={show}
            onHide={() => onCancelHandler()}
            centered
            backdrop='static'
            closeButton
            headerClassName='border-0'
            className='modal-lg'
            title={resources.modalTitle}>
            <form onSubmit={handleSubmit((inputs) => onSubmitHandler(inputs))}>

                <div className='d-flex align-items-center justify-content-between w-100'>
                    <div className='tracking-tight text-lg fw-bold text-black'>
                        {resources.orderLabel} #{item?.orderNo}
                    </div>
                    <div className='d-flex align-items-center gap-2'>
                        <span className={`${getStatusClass(item?.orderSystemStatusId ?? 0, orderStatusClasses, {
                            defaultClass: 'badge bg-light-blue'
                        })} p-2 rounded-pill text-xs`}>
                            <CircleFill width={7} height={6} /> {item?.statusName}
                        </span>
                    </div>
                </div>
                <p className='text-sm text-muted-foreground mt-1'>{item?.createdOn ? getLocalDate(item.createdOn, "DD MMM YYYY hh:mm A") : '_'}</p>

                <hr></hr>

                <div className='row text-black fw-bold'>
                    <div className='col'>
                        {resources.customerLabel}
                    </div>
                    <div className='col'>
                        {resources.orderInfoLabel}
                    </div>
                </div>

                <div className='row'>
                    <div className='col'>
                        {item?.customerName}
                    </div>
                    <div className='col'>
                        <BoxSeam width={15} />  {item?.itemsList.reduce((sum, currentItem) => sum + currentItem.quantity, 0)}
                    </div>
                </div>

                <div className='row'>
                    <div className='col'>
                        <Telephone width={15} />  {item?.phoneNumber?.replace(/^(\+)?0+/, '')}
                    </div>
                    <div className='col'>
                        <Calendar width={15} /> {item?.createdOn ? getLocalDate(item.createdOn, "DD MMM YYYY") : '_'}
                    </div>
                </div>

                <hr></hr>

                <div>
                    <span className='fw-bold text-black fs-6'>
                        {resources.itemsLabel}
                    </span>
                </div>
                {item?.itemsList.map((product, index) => (
                    <>
                        <div className={`d-flex align-items-center justify-content-between w-100 px-3 mt-3`}>
                            <div className='tracking-tight text-lg '>
                                {product.productName}
                            </div>
                            <div className='d-flex align-items-center gap-2 text-black fw-bold'>
                                {product.price} {item.currency}
                            </div>
                        </div>

                        <div className='d-flex align-items-center justify-content-between w-100 px-3'>
                            <div className='tracking-tight text-lg f-s-12 '>
                                {resources.quantityLabel}: {product.quantity}
                            </div>
                        </div>
                    </>
                ))}

                <div className='d-flex align-items-center justify-content-between w-100 mt-3'>
                    <div className='tracking-tight text-lg fw-bold text-black'>
                        {resources.totalLabel}
                    </div>
                    <div className='d-flex align-items-center gap-2'>
                        {item?.amount} {item?.currency}
                    </div>
                </div>

                <hr />

                <div className='d-grid gap-4 f-s-14'>
                    <CustomSelectField dir='auto'
                        label={resources.statusLabel}
                        inputProps={{ ...register('statusId') }}
                        itemsList={selectStatusList}
                        isLoading={isFetchingStatusList}
                        value={statusWatch}
                        defaultOptionValue=''
                        size='sm'
                        disabled={(item?.isSystemStatus && (item.orderStatusTypeId === ORDER_STATUS.Completed || item.orderStatusTypeId === ORDER_STATUS.Cancelled))}
                    />

                    <div className='form-group'>
                        <FormLabel label={resources.noteLabel} />
                        <textarea
                            {...register('note')}
                            className={`form-control rounded-12`}
                            placeholder={resources.notePlaceholder}
                            rows={3}
                        />
                    </div>

                    <ModalFooter className='border-0'>
                        <button type='button' className='btn btn-secondary ms-3' onClick={() => onCancelHandler()}>
                            {resources.cancelButton}
                        </button>
                        <SubmitButton label={resources.submitButton} isLoading={isLoading}
                            disabled={!isValid || isLoading || (item?.note === noteWatch && item?.statusId.toString() === statusWatch)} />
                    </ModalFooter>
                </div>
            </form>
        </ModalWrapper>
    );
};

export default UpdateOrderModal;